import React, { useCallback, useEffect, useRef, useState } from 'react'
import axios from 'axios'
import Select from 'react-select'
import { t } from '../../i18n'
import StatusBadge from '../shared/StatusBadge'
import EmptyTablePlaceholder from '../shared/EmptyTablePlaceholder'
import extractServerErrors from '../shared/extractServerErrors'
import { SelectOption } from '../shared/selectOptions'
import TestCaseComposer from './TestCaseComposer'
import {
  describeStep,
  LocationContext,
  LocationOption,
  TestCase,
  TestCaseAttributes
} from './models'

interface MoveModeTestingPageProps {
  locations: LocationOption[]
}

type BadgeVariant = React.ComponentProps<typeof StatusBadge>['variant']

const MoveModeTestingPage: React.FC<MoveModeTestingPageProps> = ({
  locations
}) => {
  const [locationId, setLocationId] = useState<number | null>(
    locations.length === 1 ? locations[0].id : null
  )
  const [context, setContext] = useState<LocationContext | null>(null)
  const [loading, setLoading] = useState(false)
  const [editing, setEditing] = useState<TestCase | 'new' | null>(null)
  const [saving, setSaving] = useState(false)
  const [composerErrors, setComposerErrors] = useState<string[]>([])
  const [pageErrors, setPageErrors] = useState<string[]>([])
  const [runOrderId, setRunOrderId] = useState<number | null>(null)
  const [busy, setBusy] = useState(false)
  // Responses for a location the user has already switched away from are dropped, so a
  // slow answer for kitchen A can never render as kitchen B's context - whether it comes
  // from the initial load or from an action's reload
  const contextRequest = useRef(0)
  const currentLocationId = useRef(locationId)

  const extractErrors = (error: unknown): string[] =>
    extractServerErrors(error, t('move_mode_test_cases.request_failed'))

  const loadContext = useCallback((id: number) => {
    if (id !== currentLocationId.current) return

    contextRequest.current += 1
    const requestNumber = contextRequest.current
    const isCurrent = () => requestNumber === contextRequest.current
    setLoading(true)
    setPageErrors([])
    axios
      .get<LocationContext>('/admin/move_mode_test_cases/location_context', {
        params: { location_id: id }
      })
      .then((resp) => {
        if (isCurrent()) setContext(resp.data)
      })
      .catch((error) => {
        if (isCurrent()) setPageErrors(extractErrors(error))
      })
      .finally(() => {
        if (isCurrent()) setLoading(false)
      })
  }, [])

  useEffect(() => {
    currentLocationId.current = locationId
    setContext(null)
    setEditing(null)
    setRunOrderId(null)
    if (locationId !== null) loadContext(locationId)
  }, [locationId, loadContext])

  const locationOptions: SelectOption[] = locations.map((location) => ({
    value: location.id,
    label: location.organizationName
      ? `${location.name} (${location.organizationName})`
      : location.name
  }))

  const testingSessionActive =
    context?.session?.serviceType === 'move_mode_testing' &&
    context.session.state === 'active'

  const actionsBlocked = busy || saving

  // Every page-level action: block the other buttons, run, reload this kitchen, report
  const performAction = (request: (id: number) => Promise<unknown>) => {
    if (locationId === null || actionsBlocked) return
    const id = locationId
    setBusy(true)
    setPageErrors([])
    request(id)
      .then(() => loadContext(id))
      .catch((error) => {
        if (id === currentLocationId.current)
          setPageErrors(extractErrors(error))
      })
      .finally(() => setBusy(false))
  }

  const sessionRequest = (path: string) =>
    performAction((id) =>
      axios.post(`/admin/move_mode_test_cases/${path}`, { location_id: id })
    )

  const deleteTestCase = (testCase: TestCase) => {
    if (!window.confirm(t('move_mode_test_cases.delete_confirm'))) return

    performAction((id) =>
      axios.delete(`/admin/move_mode_test_cases/${testCase.id}`, {
        params: { location_id: id }
      })
    )
  }

  const runTestCase = (testCase: TestCase) => {
    setRunOrderId(null)
    performAction((id) =>
      axios
        .post<{ orderId: number }>(
          `/admin/move_mode_test_cases/${testCase.id}/run`,
          { location_id: id }
        )
        .then((resp) => setRunOrderId(resp.data.orderId))
    )
  }

  const saveTestCase = (attributes: TestCaseAttributes) => {
    if (locationId === null || editing === null) return
    const id = locationId
    setSaving(true)
    setComposerErrors([])

    const payload = {
      location_id: id,
      move_mode_test_case: {
        name: attributes.name,
        notes: attributes.notes,
        cooking_unit_id: attributes.cookingUnitId,
        unload_move_mode_id: attributes.unloadMoveModeId,
        steps: attributes.steps.map((step) => ({
          kind: step.kind,
          product_id: step.productId,
          amount: step.amount,
          move_mode_id: step.moveModeId,
          duration_seconds: step.durationSeconds,
          stove_level: step.stoveLevel
        }))
      }
    }

    const request =
      editing === 'new'
        ? axios.post('/admin/move_mode_test_cases', payload)
        : axios.put(`/admin/move_mode_test_cases/${editing.id}`, payload)

    request
      .then(() => {
        if (id !== currentLocationId.current) return
        setEditing(null)
        loadContext(id)
      })
      .catch((error) => setComposerErrors(extractErrors(error)))
      .finally(() => setSaving(false))
  }

  const stepSummary = (testCase: TestCase, ctx: LocationContext): string =>
    testCase.steps.map((step) => describeStep(step, ctx, 'compact')).join(' → ')

  const renderSessionBar = (ctx: LocationContext) => {
    let badge: { label: string; variant: BadgeVariant }
    let hint: string
    let action: { label: string; className: string; path: string } | null = null

    if (testingSessionActive) {
      badge = {
        label: t('move_mode_test_cases.session_active_badge'),
        variant: 'success-outline'
      }
      hint = t('move_mode_test_cases.session_active')
      action = {
        label: t('move_mode_test_cases.finish_session'),
        className: 'btn btn-danger btn-sm inverted',
        path: 'finish_testing_session'
      }
    } else if (ctx.session) {
      badge = {
        label: t('move_mode_test_cases.session_other_badge', {
          type: ctx.session.serviceType.replace(/_/g, ' '),
          state: ctx.session.state
        }),
        variant: 'danger-soft'
      }
      hint = t('move_mode_test_cases.session_other')
    } else {
      badge = {
        label: t('move_mode_test_cases.session_none_badge'),
        variant: 'neutral-outline'
      }
      hint = t('move_mode_test_cases.run_requires_session')
      action = {
        label: t('move_mode_test_cases.start_session'),
        className: 'btn btn-primary btn-sm',
        path: 'start_testing_session'
      }
    }

    return (
      <div className="move-mode-testing__session-bar">
        <div className="move-mode-testing__session-info">
          <StatusBadge label={badge.label} variant={badge.variant} />
          <span className="move-mode-testing__session-hint">{hint}</span>
        </div>
        {action && (
          <button
            type="button"
            className={action.className}
            disabled={actionsBlocked}
            onClick={() => sessionRequest(action.path)}
          >
            {action.label}
          </button>
        )}
      </div>
    )
  }

  const renderTestCaseTable = (ctx: LocationContext) => {
    if (ctx.testCases.length === 0) {
      return (
        <EmptyTablePlaceholder
          heading={t('move_mode_test_cases.no_test_cases')}
          subtext={t('move_mode_test_cases.no_test_cases_subtext')}
        />
      )
    }

    return (
      <table className="table table-robis">
        <thead className="thead-robis">
          <tr>
            <th scope="col">{t('move_mode_test_cases.name')}</th>
            <th scope="col">{t('move_mode_test_cases.cooker')}</th>
            <th scope="col">{t('move_mode_test_cases.steps')}</th>
            <th scope="col" />
          </tr>
        </thead>
        <tbody>
          {ctx.testCases.map((testCase) => (
            <tr key={testCase.id}>
              <td>{testCase.name}</td>
              <td>
                {t('move_mode_test_cases.cooker_option', {
                  number:
                    ctx.cookingUnits.find(
                      (unit) => unit.id === testCase.cookingUnitId
                    )?.slotNumber ?? '?'
                })}
              </td>
              <td>
                <div className="move-mode-testing__steps-summary">
                  {stepSummary(testCase, ctx)}
                </div>
              </td>
              <td className="text-end">
                <button
                  type="button"
                  className="btn btn-primary btn-sm me-3"
                  disabled={!testingSessionActive || actionsBlocked}
                  title={
                    testingSessionActive
                      ? undefined
                      : t('move_mode_test_cases.run_requires_session')
                  }
                  onClick={() => runTestCase(testCase)}
                >
                  {t('move_mode_test_cases.run')}
                </button>
                <button
                  type="button"
                  className="btn btn-link btn-sm"
                  disabled={actionsBlocked}
                  onClick={() => {
                    setComposerErrors([])
                    setEditing(testCase)
                  }}
                >
                  {t('move_mode_test_cases.edit')}
                </button>
                {' | '}
                <button
                  type="button"
                  className="btn btn-link btn-sm"
                  disabled={actionsBlocked}
                  onClick={() => deleteTestCase(testCase)}
                >
                  {t('move_mode_test_cases.delete')}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    )
  }

  return (
    <div className="row">
      <div className="col-md-12">
        <div className="move-mode-testing__header">
          <div className="section-heading ms-3">
            <div className="section-heading-row">
              {t('move_mode_test_cases.title_prefix')}
            </div>
            <div className="section-heading-row bold">
              {t('move_mode_test_cases.title')}
            </div>
          </div>
          <div className="move-mode-testing__controls">
            <div className="move-mode-testing__location-select">
              <Select<SelectOption>
                className="react-select"
                classNamePrefix="react-select"
                placeholder={t('move_mode_test_cases.select_location')}
                options={locationOptions}
                value={
                  locationOptions.find(
                    (option) => option.value === locationId
                  ) || null
                }
                onChange={(option) => setLocationId(option?.value ?? null)}
              />
            </div>
            {context && editing === null && (
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => setEditing('new')}
              >
                {t('move_mode_test_cases.new_test_case')}
              </button>
            )}
          </div>
        </div>

        {pageErrors.length > 0 && (
          <div className="alert alert-danger" role="alert">
            {pageErrors.join(', ')}
          </div>
        )}

        {loading && <div>{t('move_mode_test_cases.loading')}</div>}

        {/* Stays mounted through reloads so an open composer keeps its unsaved edits */}
        {context && (
          <>
            {renderSessionBar(context)}

            {runOrderId !== null && (
              <div className="alert alert-success" role="alert">
                {t('move_mode_test_cases.run_started', { id: runOrderId })}
              </div>
            )}

            {editing !== null && (
              <TestCaseComposer
                key={editing === 'new' ? 'new' : editing.id}
                context={context}
                testCase={editing === 'new' ? null : editing}
                saving={saving}
                errors={composerErrors}
                onSave={saveTestCase}
                onCancel={() => {
                  setEditing(null)
                  setComposerErrors([])
                }}
              />
            )}

            {renderTestCaseTable(context)}
          </>
        )}
      </div>
    </div>
  )
}

export default MoveModeTestingPage
